import { Component, OnDestroy, OnInit } from '@angular/core';
import { TAccount } from 'src/app/project/modules/account/account.model';
import { TUser } from 'src/app/project/modules/user/user.model';
import { TWorkspace, TWorkspacesById } from 'src/app/project/modules/workspace/workspace.model';

import { SortingService } from '@core/helpers';
import VAlert from '@voly/ui-components/src/v2/VAlert';
import VNumberInput, { NumberInputProps } from '@voly/ui-components/src/v2/VNumberInput';
import { Observable, Subject, merge, of, timer } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { EStatusCode } from 'src/app/core/helpers/error-codes';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { EFeatureFlag } from 'src/app/core/services/feature-flags-consts';
import { TItemToAdd, TSelectItem } from 'src/app/project/components/input/select/select.model';
import { Modal, ModalService } from 'src/app/project/components/modal/modal.service';
import { PromptService } from 'src/app/project/components/prompt/prompt.service';
import { TranslationPipe } from 'src/app/project/features/translate/translation.pipe';
import { AccountService } from 'src/app/project/modules/account/account-service/account.service';
import { AttachmentsFilesService } from 'src/app/project/modules/points/attachments/attachments-files.service';
import { PointsService } from 'src/app/project/modules/points/points.service';
import { EUserRole } from 'src/app/project/modules/share/share-utils/user-roles';
import { UserService } from 'src/app/project/modules/user/user.service';
import { WorkspaceService } from 'src/app/project/modules/workspace/workspace.service';
import { EBasicField } from 'src/app/project/shared/enums/basic-fields-enums';
import { iconMap } from 'src/app/project/shared/enums/icon-map';
import { EIconPath } from '../../../../shared/enums/icons.enum';
import { ECustomFieldType } from '../../../custom-fields/custom-field-types-enums';
import { EIntegrationStatus } from '../../../custom-fields/custom-fields.model';
import { CustomFieldsService } from '../../../custom-fields/custom-fields.service';
import { CustomTableService } from '../../site-table/custom-table/custom-table.service';
import { LONG_PROCESSING_TIME_MS } from '../site-options';
import { TFieldConflict } from '../site-options.model';
import { getFieldConflicts } from '../utils/getFieldConflicts';
import { ECopyMoveModalType } from './copy-move-modal-type..enum';
import {
  ECopyMoveItems,
  GET_DEFAULT_INCLUDE_ITEMS,
  TCopyMoveOptions,
  TIncludeItem,
} from './copy-move-options';
import { TPointsCopyModalData, TPointsMoveModalData } from './points-copy-move-modal.model';
import { PointsCopyMoveModalService } from './points-copy-move-modal.service';
@Component({
  selector: 'pp-points-copy-move-modal',
  templateUrl: './points-copy-move-modal.component.html',
  styleUrls: ['../pointCopyMove.scss'],
  standalone: false,
})
export class PointsCopyMoveModalComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly pointCopied$ = new Subject<void>();

  modal: Modal = this.modalService.getModal();
  modalData: TPointsCopyModalData | TPointsMoveModalData;

  modalType: ECopyMoveModalType = ECopyMoveModalType.COPY;
  ECopyMoveModalType = ECopyMoveModalType;
  ECustomFieldType = ECustomFieldType;
  EBasicField = EBasicField;
  EIconPath = EIconPath;

  readonly minCopies = 1;
  readonly maxTotalCopies = 200;
  readonly maxAttachmentSizeMB = 200;
  numberOfCopies = this.minCopies;
  totalNumberOfCopies = this.minCopies;
  hasMultipleCopiesFeatureFlag = false;

  NumberInput = VNumberInput;
  VAlert = VAlert;
  vAlertProps = {
    title: this.translationPipe.transform('points_with_photos_will_be_by_email'),
    size: 'sm',
    variant: 'surface',
    p: 4,
  };

  vAlertIntegrationProps = {
    description: '',
    size: 'sm',
    variant: 'surface',
    status: 'warning',
    p: 4,
  };

  vAlertRelatedPointsProps = {
    description: '',
    size: 'sm',
    variant: 'surface',
    status: 'warning',
    p: 4,
  };

  vAlertErrorProps = {
    title: this.translationPipe.transform('limit_exceeded'),
    description: this.translationPipe.transform('attachment_limit_message', {
      maxSize: this.maxAttachmentSizeMB,
    }),
    size: 'sm',
    variant: 'surface',
    status: 'error',
    p: 4,
  };
  numberInputProps: NumberInputProps = {
    value: this.numberOfCopies,
    min: this.minCopies,
    onValueChange: (value: number): void => this.onNumberOfCopiesChange(value),
    maxDecimalPlaces: 0,
  };

  processing = false;
  public accounts: TAccount[];
  public activeWorkspace: TWorkspace;
  public workspaces: TWorkspacesById;
  selectedWorkspaceId: string = null;
  selectedWorkspaceName: string = null;
  user: TUser;
  sizeMB = 0;
  longProcessing = false;
  totalSize: number;
  firstStep = true;
  secondStep = false;
  integrationWarning: boolean;
  hasExceededMaxCopiesLimit = false;
  hasRelatedPoints = false;

  fieldConflicts: TFieldConflict[] = [];

  pointPinsToBeCentered = false;
  iconMap = iconMap;
  items: TSelectItem[] = [
    {
      label: '',
      options: [],
    },
  ];

  itemsToInclude: TIncludeItem[] = GET_DEFAULT_INCLUDE_ITEMS();

  readonly maxAttachmentsSize = this.maxAttachmentSizeMB * 1000000;

  bypassPointsUploadLimit = false;

  constructor(
    private accountService: AccountService,
    private workspaceService: WorkspaceService,
    private pointsService: PointsService,
    private attachmentsFilesService: AttachmentsFilesService,
    private sortingService: SortingService,
    private promptService: PromptService,
    private modalService: ModalService,
    private pointsCopyMoveModalService: PointsCopyMoveModalService,
    private userService: UserService,
    private translationPipe: TranslationPipe,
    private customFieldsService: CustomFieldsService,
    private featureFlagService: FeatureFlagService,
    private customTableService: CustomTableService,
  ) {}

  ngOnInit() {
    this.accounts = this.accountService.getAccounts();
    this.workspaces = this.workspaceService.getWorkspaces();
    this.modalData = this.modal.data;
    this.activeWorkspace = this.workspaceService.getWorkspace(this.modalData.workspaceId);
    this.user = this.userService.getUser();
    this.modalType = this.modalData.modalType;
    const account = this.accountService.getAccount(this.activeWorkspace.accountId);
    this.bypassPointsUploadLimit = this.user.userId === account?.accountOwnerId;

    this.handleFeatureFlagChanges();

    this.integrationWarning = !!this.activeWorkspace.customFields?.some((customFieldId) => {
      const customField = this.customFieldsService.getWorkspaceCustomField(
        this.activeWorkspace.workspaceId,
        customFieldId,
      );

      return customField.volyIntegrationActive !== EIntegrationStatus.OFF;
    });

    this.generateWorkspaceList();

    this.orderListItems();

    this.fetchInitialSizes();

    this.hasRelatedPoints = this.modalData.hasRelatedPoints ?? false;

    this.vAlertRelatedPointsProps = {
      ...this.vAlertRelatedPointsProps,
      description:
        this.modalType === ECopyMoveModalType.COPY
          ? this.translationPipe.transform('related_points_copy')
          : this.translationPipe.transform('related_points_move'),
    };

    this.vAlertIntegrationProps = {
      ...this.vAlertIntegrationProps,
      description: this.getIntegrationWarningDescription(),
    };
  }

  private getIntegrationWarningDescription(): string {
    const isCopy = this.modalType === ECopyMoveModalType.COPY;

    if (this.hasRelatedPoints) {
      return isCopy
        ? this.translationPipe.transform('copy_point_integration_warning_related_points')
        : this.translationPipe.transform('move_point_integration_warning_related_points');
    }

    return isCopy
      ? this.translationPipe.transform('copy_point_integration_warning')
      : this.translationPipe.transform('move_point_integration_warning');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  handleFeatureFlagChanges(): void {
    this.hasMultipleCopiesFeatureFlag = this.featureFlagService.getFlag(
      EFeatureFlag.COPY_MULTIPLE_POINTS,
    );

    this.featureFlagService.flagsChange$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.hasMultipleCopiesFeatureFlag = this.featureFlagService.getFlag(
        EFeatureFlag.COPY_MULTIPLE_POINTS,
      );
    });
  }

  get currentSizeWithAttachments(): number {
    return this.sizeMB * this.numberOfCopies;
  }

  get exceedsAttachmentSizeLimit(): boolean {
    return (
      !this.bypassPointsUploadLimit &&
      (this.totalSize ?? 0) * this.numberOfCopies > this.maxAttachmentsSize
    );
  }

  get hasExceededMaxAttachmentSize(): boolean {
    return (
      !this.bypassPointsUploadLimit && this.currentSizeWithAttachments > this.maxAttachmentSizeMB
    );
  }

  secondStepDisabled(): boolean {
    const copyingAboveLimit = this.hasMultipleCopiesFeatureFlag && this.hasExceededMaxCopiesLimit;

    return !this.selectedWorkspaceId || copyingAboveLimit || this.exceedsAttachmentSizeLimit;
  }

  onNumberOfCopiesChange(value: number): void {
    this.numberOfCopies = Number.isFinite(value) ? value : 0;

    const numberOfPoints = this.getNumberOfPoints();
    this.totalNumberOfCopies = numberOfPoints
      ? this.numberOfCopies * numberOfPoints
      : this.numberOfCopies;

    this.hasExceededMaxCopiesLimit =
      !this.bypassPointsUploadLimit && this.totalNumberOfCopies > this.maxTotalCopies;

    this.numberInputProps = { ...this.numberInputProps, value };
  }

  orderListItems(): void {
    this.items.sort((a, b) => this.sortingService.naturalSort(a.label, b.label));

    this.items.forEach((account) => {
      account.options.sort((a, b) => this.sortingService.naturalSort(a.name, b.name));
    });

    if (this.modalType === ECopyMoveModalType.COPY) {
      this.items.unshift({
        label: this.activeWorkspace.siteName,
        value: {
          name: this.activeWorkspace.siteName,
          id: this.activeWorkspace.workspaceId,
          enabled: true,
          message: 'current_site',
        },
        options: [],
      });
    }
  }

  private generateWorkspaceList(): void {
    this.accounts.forEach((account) => {
      const workspaces: TItemToAdd[] = [];

      account.workspaces.forEach((workspaceId) => {
        const isUserWhoCanCopyOrMove =
          this.workspaces[workspaceId].share.shareOption === EUserRole.OWNER ||
          this.workspaces[workspaceId].share.shareOption === EUserRole.SITE_ADMIN ||
          this.workspaces[workspaceId].share.shareOption === EUserRole.ACCOUNT_ADMIN ||
          this.workspaces[workspaceId].share.shareOption === EUserRole.NORMAL;
        const isActiveWorkspace = this.activeWorkspace.workspaceId === workspaceId;

        if (
          isUserWhoCanCopyOrMove &&
          (this.modalType === ECopyMoveModalType.COPY || !isActiveWorkspace)
        ) {
          const itemToAdd: TItemToAdd = {
            name: this.workspaces[workspaceId].siteName,
            id: workspaceId,
            enabled: true,
            message: this.activeWorkspace.workspaceId === workspaceId ? 'current_site' : null,
          };

          workspaces.push(itemToAdd);
        } else if (!isActiveWorkspace) {
          const itemToAdd: TItemToAdd = {
            name: this.workspaces[workspaceId].siteName,
            id: workspaceId,
            enabled: false,
            disabledMessage: 'you_are_not_admin_normal',
          };

          workspaces.push(itemToAdd);
        } else {
          const itemToAdd: TItemToAdd = {
            name: this.workspaces[workspaceId].siteName,
            id: workspaceId,
            enabled: false,
            message: this.activeWorkspace.workspaceId === workspaceId ? 'current_site' : null,
            disabledMessage: 'cant_move_to_the_same_site',
          };

          workspaces.push(itemToAdd);
        }
      });

      if (workspaces.length > 0) {
        this.items.push({
          label: account.accountName,
          options: workspaces,
        });
      }
    });
  }

  hideModal(): void {
    this.modalService.hideModal();
  }

  selectWorkspace(selectedWorkspaceId: string, selectedWorkspaceName: string): void {
    this.selectedWorkspaceId = selectedWorkspaceId;
    this.selectedWorkspaceName = selectedWorkspaceName;
  }

  selectItem(item: { id: string; name: string }): void {
    this.selectedWorkspaceId = item.id;
    this.selectedWorkspaceName = item.name;
  }

  check(): void {
    this.processing = true;
    const sourceWorkspaceId = this.modalData.workspaceId;

    this.pointsCopyMoveModalService
      .checkTargetSite(sourceWorkspaceId, this.selectedWorkspaceId)
      .pipe(
        takeUntil(this.destroy$),
        tap((response) => {
          const conflictsFound =
            response.missingCustomFields.COST ||
            response.missingCustomFields.DATE ||
            response.missingCustomFields.LIST ||
            response.missingCustomFields.MULTI_LIST ||
            response.missingCustomFields.TEXT ||
            response.missingCustomFields.RICHTEXT ||
            response.missingCustomFields.CHECKBOX ||
            response.missingCustomFields.TIME ||
            response.missingCustomFields.NUMBERS ||
            response.missingCustomFields.PERCENTAGE ||
            response.missingCustomFields.TIMELINE ||
            response.missingCustomFields.FORMULA ||
            response.missingTags.length ||
            response.matchingSitePlans === false;

          this.firstStep = false;
          this.secondStep = true;

          if (response.matchingSitePlans === false) {
            this.pointPinsToBeCentered = true;
          }

          if (conflictsFound) {
            this.fieldConflicts.push(...getFieldConflicts(response));
          }
        }),
        catchError((error) => {
          let promptText = this.translationPipe.transform(
            this.modalType === ECopyMoveModalType.COPY
              ? 'prompt_copy_failed'
              : 'prompt_points_move_error',
          );

          if (error.status === EStatusCode.CONFLICT) {
            promptText = this.translationPipe.transform('prompt_request_in_progress');
          }

          this.promptService.showError(promptText);

          return of();
        }),
        finalize(() => {
          this.processing = false;
        }),
      )
      .subscribe();
  }

  confirmationButtonText(): string {
    if (this.modalType === ECopyMoveModalType.MOVE) {
      return this.translationPipe.transform('move');
    }

    const number =
      this.totalNumberOfCopies > 1 ? this.totalNumberOfCopies : Number(this.getNumberOfPoints());

    if (this.hasMultipleCopiesFeatureFlag) {
      return this.translationPipe.transform(
        number === 1 ? 'copies_number_one' : 'copies_number_other',
        { count: number },
      );
    } else {
      return this.translationPipe.transform('confirm');
    }
  }

  confirm(): void {
    const sourceWorkspaceId = this.modalData.workspaceId;
    this.processing = true;

    const pointIds = this.getPointIds();

    if (this.bypassPointsUploadLimit || this.totalSize < this.maxAttachmentsSize) {
      this.sendRequest(sourceWorkspaceId, pointIds.length, pointIds);
    } else {
      this.firstStep = false;
      this.secondStep = true;
      this.processing = false;
    }
  }

  private sendRequest(sourceWorkspaceId: string, numberOfPoints: number, pointIds: string[]): void {
    this.startLongProcessChecker();
    let request: Observable<string>;

    const options = this.getCopyMoveOptions();

    const pointsCopyParams = {
      sourceWorkspaceId,
      numberOfPoints,
      pointIds,
      selectedWorkspaceId: this.selectedWorkspaceId,
      options,
    };

    if (this.modalType === ECopyMoveModalType.COPY) {
      request = this.pointsCopyMoveModalService.copyPoints({
        ...pointsCopyParams,
        hasMultipleCopiesFeatureFlag: this.hasMultipleCopiesFeatureFlag,
      });
    } else {
      request = this.pointsCopyMoveModalService.movePoints(pointsCopyParams);
    }

    request
      .pipe(
        tap(() => {
          this.customTableService.reloadTable();
          this.hideModal();
        }),
        finalize(() => {
          this.processing = false;
          this.stopLongProcessChecker();
        }),
      )
      .subscribe();
  }

  private getCopyMoveOptions(): TCopyMoveOptions {
    const options: TCopyMoveOptions = {
      activities: true,
      comments: true,
      images: true,
      videos: true,
      documents: true,
      description: true,
    };

    if (this.hasMultipleCopiesFeatureFlag) {
      options.numberOfCopies = this.numberOfCopies;
    }

    this.itemsToInclude.forEach((item) => {
      options[item.value] = item.selected;
    });

    return options;
  }

  private startLongProcessChecker(): void {
    timer(LONG_PROCESSING_TIME_MS)
      .pipe(
        takeUntil(merge(this.pointCopied$, this.destroy$)),
        tap(() => {
          this.longProcessing = true;
        }),
      )
      .subscribe();
  }

  private stopLongProcessChecker(): void {
    this.pointCopied$.next();
    this.longProcessing = false;
  }

  updateItemsToInclude(item: TIncludeItem): void {
    item.selected = !item.selected;

    this.totalSize = this.itemsToInclude.reduce(
      (acc, itemToInclude) => acc + (itemToInclude.selected ? itemToInclude.size : 0),
      0,
    );

    this.sizeMB = Math.round(+this.totalSize / 1000000);
  }

  private fetchInitialSizes(): void {
    const options: TCopyMoveOptions = {
      activities: true,
      comments: true,
      images: true,
      videos: true,
      documents: true,
      description: true,
    };

    this.processing = true;

    const pointIds: string[] = this.getPointIds();

    const sourceWorkspaceId = this.modalData.workspaceId;

    this.attachmentsFilesService
      .fetchPointSizes(pointIds, sourceWorkspaceId, this.selectedWorkspaceId, options)
      .pipe(
        takeUntil(this.destroy$),
        tap((size) => {
          this.totalSize = size.documents + size.videos + size.images;
          this.sizeMB = Math.round(+this.totalSize / 1000000);

          this.itemsToInclude.forEach((item) => {
            switch (item.value) {
              case ECopyMoveItems.FILES:
                item.sizeMB = Math.round(+size.documents / 1000000);
                item.size = size.documents;
                break;
              case ECopyMoveItems.VIDEOS:
                item.sizeMB = Math.round(+size.videos / 1000000);
                item.size = size.videos;
                break;
              case ECopyMoveItems.PHOTOS:
                item.sizeMB = Math.round(+size.images / 1000000);
                item.size = size.images;
                break;
              default:
                break;
            }
          });

          this.processing = false;
        }),
      )
      .subscribe();
  }

  private getNumberOfPoints(): number | undefined {
    return 'numberOfPoints' in this.modalData ? this.modalData.numberOfPoints : undefined;
  }

  private getPointIds(): string[] {
    let pointIds: string[];
    if (this.modalData.selectedPoint) {
      pointIds = [this.modalData.selectedPoint._id];
    } else {
      const selectedPoints = this.pointsService.getSelectedPoints();
      pointIds = selectedPoints.map((point) => point._id);
    }
    return pointIds;
  }
}
